import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteProduct, loadAllProdutcs } from "../../../../services/products";
import type { IResultProducts } from "../../../../types/products";
import Button from "../../../../components/Button/Button";
import TableContainer from "../../../../components/TableContainer/TableContainer";
import HeaderProducts from "./HeaderProducts";
import styles from "./Products.module.css";
import useStore from "./Values";
// Importações necessárias para o novo modal
import ModalDelete from "../../../../components/ModalDelete/ModalDelete";
import { useIsMobile } from "../../../../utils/useIsMobile";

type fieldOrder =
  | "sku"
  | "codigo_de_barras"
  | "titulo"
  | "medida"
  | "categoria"
  | "preco";

interface IorderProduct {
  sku?: "ASC" | "DESC";
  codigo_de_barras?: "ASC" | "DESC";
  titulo?: "ASC" | "DESC";
  medida?: "ASC" | "DESC";
  categoria?: "ASC" | "DESC";
  preco?: "ASC" | "DESC";
}

const Products = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const setCopyProduct = useStore((state) => state.setCopyProduct);

  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<any>({});
  const [orderTable, setOrderTable] = useState<IorderProduct>({ sku: "ASC" });
  
  // Estado para controlar o modal de deleção
  const [deleteTarget, setDeleteTarget] = useState<{ uid: string; titulo: string } | null>(null);

  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery<IResultProducts>({
      queryKey: ["loadProducts", search, filterValues, orderTable],
      queryFn: ({ pageParam = 1 }) =>
        loadAllProdutcs(search, pageParam as number, filterValues, orderTable),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore ? allPages.length + 1 : undefined,
      initialPageParam: 1,
    });

  const { mutate, isPending } = useMutation({
    mutationFn: (uid_produto: string) => deleteProduct(uid_produto),
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ["loadProducts"] });
      setDeleteTarget(null); // Fecha o modal após sucesso
    },
    onError: (err: any) => {
      toast.error(err.message);
    },
  });

  const handleDelete = (item: any) => {
    setDeleteTarget({
        uid: item.tbl_produto?.uid_produto,
        titulo: item.tbl_produto?.titulo
    });
  };

  const handleChangeOrder = (field: fieldOrder) => {
    setOrderTable({
      [field]: orderTable[field] === "ASC" ? "DESC" : "ASC",
    });
  };

  const renderSortableHeader = (field: fieldOrder, label: string) => {
    return (
      <Button
        variant="textButton"
        onClick={() => { handleChangeOrder(field); }}
        title={orderTable[field] === "DESC" ? "Decrescente" : "Crescente"}
      >
        {label}
        {orderTable[field] ? (
          orderTable[field] === "DESC" ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />
        ) : ("")}
      </Button>
    );
  };

  return (
    <div className={styles.containerProducts}>
      <div className={styles.products}>
        <HeaderProducts
          search={search}
          setSearch={setSearch}
          cleanFilters={() => {
            setFilterValues({});
            setSearch("");
          }}
          qtdProducts={data?.pages?.[0]?.qtdProduct || 0}
          setFilterValues={setFilterValues}
          filterValues={filterValues}
        />

        <TableContainer
          columns={[
            { key: "skuEx", titulo: renderSortableHeader("sku", "SKU"), classStyle: styles.col_sku },
            { key: "barcodeEx", titulo: renderSortableHeader("codigo_de_barras", "Código"), classStyle: styles.col_barcode },
            { key: "tituloEx", titulo: renderSortableHeader("titulo", "Título do Produto"), classStyle: styles.col_title },
            { key: "medidaEx", titulo: renderSortableHeader("medida", "Medida"), classStyle: styles.col_measure },
            { key: "categoriaEx", titulo: renderSortableHeader("categoria", "Categoria"), classStyle: styles.col_category },
            { key: "precoEx", titulo: renderSortableHeader("preco", "Preço"), classStyle: styles.col_price },
            { key: "actionEx", titulo: "Ação", classStyle: styles.col_action },
          ]}
          content={
            isLoading
              ? [{ values: { skuEx: "Carregando..." }, buttons: [] }]
              : (data?.pages?.flatMap((page) =>
                  page.product.map((item) => ({
                    onClick: () => navigate(`edit/${item.tbl_produto?.uid_produto}`),
                    values: {
                      skuEx: item.tbl_produto?.codigo_sku,
                      barcodeEx: item.tbl_produto?.codigo_de_barras,
                      tituloEx: item.tbl_produto?.titulo,
                      medidaEx: item.tbl_produto.tbl_tipo_unidade_medida?.titulo,
                      categoriaEx: item.tbl_produto.tbl_produto_categoria?.titulo,
                      precoEx: item.preco.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }),
                    },
                    buttons: [
                      {
                        variant: "contain",
                        typeStyle: "small",
                        title: "Copiar dados",
                        onClick: () => {
                          setCopyProduct(item.tbl_produto?.uid_produto);
                          navigate("add");
                        },
                        icon: <ContentCopyIcon />,
                      },
                      {
                        variant: "contain",
                        typeStyle: ["small", "cancel"],
                        title: "Deletar produto",
                        // Passando o objeto inteiro para o handleDelete
                        onClick: () => handleDelete(item), 
                        icon: <DeleteIcon />,
                      },
                    ],
                  }))
                ) ?? [])
          }
        />

        <div className={styles.loadMore}>
          {data?.pages &&
            data.pages.length > 0 &&
            (hasNextPage ? (
              <Button variant="contain" onClick={() => fetchNextPage()}>
                Carregar Mais produtos
              </Button>
            ) : (
              <p className={styles.tableLastItem}>
                Todos os produtos foram carregados
              </p>
            ))}
        </div>
      </div>

      {/* Novo Modal de Deleção */}
      <ModalDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && mutate(deleteTarget.uid)}
        isDeleting={isPending}
        entity="produto"
        title={deleteTarget?.titulo || ""}
        isMobile={isMobile}
      />
    </div>
  );
};

export default Products;